import React, { useMemo } from 'react';
import Grid from "@mui/material/Grid";
import QATable from "../Processing/Tables/QualityAssurance";
import Receivals from "../Processing/Tables/Receivals";
import { textfieldDateFormat, shiftHours, dateFormat } from "../../utils/dateFunctions";
import { numberFormat } from '../../utils/numberFunctions';
import DatePicker from "../../shared/DatePicker";
import { useJumboTheme } from "@jumbo/hooks";
import Button from '@mui/material/Button';
import { TableRow, TableFooter, TableCell, Typography, CircularProgress } from '@mui/material';
import SupplierSelect from 'app/components/SupplierSelect';
import EstuaryStatusSelect from 'app/components/EstuaryStatusSelect';
import HarvestAreaSelect from 'app/components/HarvestAreaSelect';
import TextField from '@mui/material/TextField';
import CustomerSelect from 'app/components/CustomerSelect';
import IconButton from '@mui/material/IconButton';
import ClearIcon from '@mui/icons-material/Clear';
import Div from "@jumbo/shared/Div";
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import JumboCardQuick from "@jumbo/components/JumboCardQuick";
import AddIcon from '@mui/icons-material/Add';
import JumboScrollbar from "@jumbo/components/JumboScrollbar";
import DataTableStandard from 'app/components/DataTableStandard';
import Link from "@mui/material/Link";
import MailIcon from '@mui/icons-material/Mail';
import DownloadIcon from '@mui/icons-material/Download';
import { convertToCSV } from 'app/utils/downloadUtils';
import Alert from '@mui/material/Alert';
import Pagination from '@mui/material/Pagination';
import { getLotReport, getTraceabilityLots, mapWithConcurrency } from './traceabilityRequests';
import useLotReport from './useLotReport';

const Traceability = (props) => {
    const [reload, setReload] = React.useState(false);
    const [startDate, setStartDate] = React.useState(shiftHours(new Date(), -24 * 7));
    const [endDate, setEndDate] = React.useState(new Date());
    const [loading, setLoading] = React.useState(false);
    const [supplier, setSupplier] = React.useState(null);
    const [estuary, setEstuary] = React.useState(null);
    const [harvestArea, setHarvestArea] = React.useState(null);
    const completed = false;
    const [customer, setCustomer] = React.useState(null);
    const [prn, setPrn] = React.useState(null);
    const [prnList, setPrnList] = React.useState([]);
    const [loadingDownload, setLoadingDownload] = React.useState(false);
    const [error, setError] = React.useState('');
    const [searched, setSearched] = React.useState(false);
    const [lotPage, setLotPage] = React.useState(1);
    const searchController = React.useRef(null);
    const downloadController = React.useRef(null);
    const selectedLots = prnList.filter(lot => lot.selected).map(lot => lot.LotNbr);
    React.useEffect(() => () => {
        searchController.current?.abort();
        downloadController.current?.abort();
    }, []);
    const reloadFunc = () => {
        setReload(!reload);
    }

    const searchTrace = async () => {
        searchController.current?.abort();
        const controller = new AbortController();
        searchController.current = controller;
        setError('');
        setLoading(true);
        setSearched(false);
        setPrnList([]);
        try {
            const dates = { StartDate: textfieldDateFormat(startDate), EndDate: textfieldDateFormat(endDate) };
            if (!prn && (!startDate || !endDate || Number.isNaN(new Date(startDate).getTime()) ||
                Number.isNaN(new Date(endDate).getTime()) || new Date(startDate) > new Date(endDate))) {
                throw new Error('Select a valid start and end date.');
            }
            let path = '/Reports/GetLotNoByDate';
            let params = dates;
            if (prn) {
                path = '/Inventory/SearchPRN';
                params = { LotNbrSubString: prn.trim() };
            } else if (customer) {
                path = '/Reports/GetLotNoByCustomer';
                params = { ...dates, CustomerId: customer.id };
            } else if (supplier) {
                path = '/Reports/GetLotNoBySupplier';
                params = { ...dates, SupplierId: supplier.id };
            } else if (harvestArea) {
                path = '/Reports/GetLotNoByHarvestArea';
                params = { ...dates, HarvestAreaId: harvestArea.id };
            } else if (estuary) {
                path = '/Reports/GetLotNoByEstuary';
                params = { ...dates, EstuaryId: estuary.id };
            }
            const lots = await getTraceabilityLots(path, params, controller);
            if (controller.signal.aborted) return;
            setPrnList(lots.map(LotNbr => ({ LotNbr, selected: true })));
            setLotPage(1);
            setSearched(true);
            reloadFunc();
        } catch (requestError) {
            if (!controller.signal.aborted) setError(requestError.message);
        } finally {
            if (!controller.signal.aborted) setLoading(false);
        }
    }
    const removePrns = (ind) => {
        var prns = [...prnList];
        prns[ind].selected = false;
        setPrnList(prns);
    }
    const addPrns = (ind) => {
        var prns = [...prnList];
        prns[ind].selected = true;
        setPrnList(prns);
    }
    const getButtonText = () => {
        if (prn) {
            return 'Search PRN: ' + prn;
        } else if (customer) {
            return 'Search Customer: ' + customer.name;
        } else if (supplier) {
            return 'Search Supplier: ' + supplier.name;
        } else if (estuary && !harvestArea) {
            return 'Search Estuary: ' + estuary.description;
        } else if (harvestArea) {
            return 'Search Harvest Area: ' + harvestArea.description;
        } else {
            return 'Search Date Range';
        }
    }
    const downloadAll = async (abortController) => {
        setLoadingDownload(true);
        setError('');
        try {
        var prmArr = [];
        var filteredPrnList = prnList.filter((prn) => {
            return prn.selected;
        });
        const lots = filteredPrnList.map(lot => lot.LotNbr);
        if (!lots.length) throw new Error('Select at least one lot to download.');
        prmArr.push(getLotReport('/Reports/GetStockOnHand', lots, {}, abortController));
        prmArr.push(getLotReport('/Reports/GetStockInProcess', lots, {}, abortController));
        prmArr.push(getLotReport('/Reports/GetCustomers', lots, {}, abortController).then(async (data) => {
            return mapWithConcurrency(data.data, async row => ({
                ...await getLotReport('/Reports/GetCustomerSalesData', lots, { customerId: row.customer.id }, abortController),
                ...row
            }), 2, abortController.signal);
        }));
        var allDataList = [];
        var returnedData = await Promise.all(prmArr);
        var invData = returnedData[0]?.data?.map((d) => {
            return {
                LotSerialNbr: d.lotSerialNbr,
                StockCode: d.stockItem?.myobid,
                Stockitem: d.stockItem?.description,
                Quantity: d.quantity,
                Warehouse: d.warehouse?.description,
                "Line/Bench": "N/A",
                Process: "N/A",
                ProductionDate: "N/A",
                "Sales Order": "N/A",
                "Shipment": "N/A",
                "Price": "N/A",
                isShipped: "N/A",
                Type: 'Inventory'
            }
        });
        var processData = returnedData[1]?.data?.map((d) => {
            return {
                LotSerialNbr: d.lotSerialNbr,
                StockCode: d.stockItem?.myobid,
                Stockitem: d.stockItem?.description,
                Quantity: d.quantity,
                Warehouse: d.warehouse?.description,
                "Line/Bench": d.processLine?.description,
                Process: d.processLine?.isHalfshell ? 'Halfshell' : 'Live',
                ProductionDate: "N/A",
                "Sales Order": "N/A",
                "Shipment": "N/A",
                "Price": "N/A",
                isShipped: "N/A",
                Type: 'Process'
            }
        });
        var custData = returnedData[2]?.flatMap((curr) => {
            return curr.orderData?.flatMap((c) => {
                return c.lines.map((d) => {
                    return {
                        LotSerialNbr: d.lotSerialNbr,
                        StockCode: d.stockItem?.myobid,
                        Stockitem: d.stockItem?.description,
                        Quantity: d.quantity,
                        Warehouse: d.warehouse?.description,
                        "Line/Bench": "N/A",
                        Process: "N/A",
                        ProductionDate: d.date,
                        "Sales Order": c.salesOrderMYOBID,
                        "Shipment": c.shipmentMYOBID,
                        "Price": d.price,
                        isShipped: c.shipmentMYOBID === 'Not Shipped' ? 'No' : 'Yes',
                        Type: 'Shipment'
                    }
                });
            }) || [];
        });

        allDataList = [...(invData || []), ...(processData || []), ...(custData || [])];
        if (abortController.signal.aborted) return;
        const csvData = new Blob([convertToCSV(allDataList)], { type: 'text/csv' });
        const csvURL = URL.createObjectURL(csvData);
        const link = document.createElement('a');
        const fileName = 'ProductRecall' + new Date().toLocaleString();
        link.href = csvURL;
        link.download = `${fileName}.csv`;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        URL.revokeObjectURL(csvURL);
        } catch (requestError) {
            if (!abortController.signal.aborted) setError(requestError.message);
        } finally {
            if (!abortController.signal.aborted) {
                setLoadingDownload(false);
            }
        }
    }
    return (
        <React.Fragment>
            <Grid container spacing={2} mb={1}>
            <Grid item xs={12} md={2}>
                </Grid>
                <Grid item xs={12} md={2}>
                    <DatePicker label={customer ? "Shipment Start Date" : "Harvest Start Date"} date={startDate} setDate={(date) => setStartDate(date)} />
                </Grid>
                <Grid item xs={12} md={2}>
                    <DatePicker label={customer ? "Shipment End Date" : "Harvest End Date"} date={endDate} setDate={(date) => setEndDate(date)} />
                </Grid>
            </Grid>
            <Grid container spacing={2} mb={1}>
                <Grid item xs={12} md={2}>
                    <TextField
                        id="outlined-required"
                        label="PRN"
                        value={prn || ''}
                        onChange={(e) => setPrn(e.target.value)}
                        noValidate
                        sx={{ width: '80%' }}
                        disabled={Boolean(customer || estuary || harvestArea || supplier || completed)}
                        InputProps={{
                            endAdornment: (<IconButton size="small" onClick={() => setPrn('')}>
                                <ClearIcon />
                            </IconButton>)
                        }}
                    />
                </Grid>

                <Grid item xs={12} md={2}>
                    <SupplierSelect widthAuto state={supplier} setState={setSupplier} disabled={Boolean(prn || customer || estuary || harvestArea || completed)} />
                </Grid>
                <Grid item xs={12} md={2}>
                    <EstuaryStatusSelect widthAuto state={estuary} setState={setEstuary} disabled={Boolean(prn || customer || supplier || harvestArea || completed)} />
                </Grid>
                <Grid item xs={12} md={2}>
                    <HarvestAreaSelect estuary={estuary} widthAuto state={harvestArea} setState={setHarvestArea} disabled={Boolean(prn || customer || supplier || completed)} />
                </Grid>
                <Grid item xs={12} md={2}>
                    <CustomerSelect widthAuto state={customer} setState={setCustomer} disabled={Boolean(prn || supplier || estuary || harvestArea || completed)} />
                </Grid>
                <Grid item xs={12} md={6}>
                    <Button onClick={searchTrace} disabled={loading || loadingDownload} variant="contained" color="primary">{getButtonText()}</Button>
                    {loading ? <CircularProgress size={24} sx={{ ml: 2 }} /> : null}
                    {selectedLots.length > 0 ? (!loadingDownload ? <IconButton title="Download traceability CSV" onClick={() => {
                        const controller = new AbortController();
                        downloadController.current = controller;
                        downloadAll(controller);
                    }}>
                        <DownloadIcon />
                    </IconButton> : <CircularProgress />) : null}
                </Grid>
            </Grid>
            {error ? <Alert severity="error">{error}</Alert> : null}
            {searched && !prnList.length ? <Alert severity="info">No lots found for the selected filters.</Alert> : null}
            <JumboScrollbar
                autoHeight
                autoHeightMax={150}
                autoHeightMin={20}
                autoHide
                autoHideDuration={200}
                autoHideTimeout={500}
            >
                <Grid container spacing={2} mb={1} style={{ marginTop: '5px', marginLeft: '10px' }}>
                    {prnList.length > 0 ? prnList.slice((lotPage - 1) * 100, lotPage * 100).map((prn, index) => {
                        const ind = (lotPage - 1) * 100 + index;
                        return <Div key={prn.LotNbr} style={{ color: (prn.selected ? 'green' : 'red') }}><Typography style={{ display: 'inline' }}>{prn.LotNbr}</Typography>{prn.selected ? <IconButton title="Exclude lot" size="small" onClick={() => removePrns(ind)}>
                            <ClearIcon />
                        </IconButton> : <IconButton title="Include lot" size="small" onClick={() => addPrns(ind)}>
                            <AddIcon />
                        </IconButton>}</Div>
                    }) : null}
                </Grid>
            </JumboScrollbar>
            {prnList.length ? <Typography variant="body2">{selectedLots.length} of {prnList.length} lots selected</Typography> : null}
            {prnList.length > 100 ? <Pagination count={Math.ceil(prnList.length / 100)} page={lotPage} onChange={(event, page) => setLotPage(page)} /> : null}
            <Div>
                <Grid container spacing={4} mb={1}>
                    <Grid item xs={12} sm={12} md={6}>
                        <InventoryReport reloadFunc={reloadFunc} prnList={prnList} reload={reload} />

                    </Grid>
                    <Grid item xs={12} sm={12} md={6}>
                        <ProcessingStatus reloadFunc={reloadFunc} prnList={prnList} reload={reload} />

                    </Grid>
                    <Grid item xs={12} sm={12} >
                        <Customers reloadFunc={reloadFunc} prnList={prnList} reload={reload} />

                    </Grid>
                </Grid>
            </Div>
                    <Grid container spacing={4} mb={1}>
                        <Grid item xs={12} sm={12} md={6}>
                            <CollapsibleSection title="QA">
                                <QATable title={" "} reloadFunc={reloadFunc} prnList={prnList} startDate={startDate} endDate={endDate} shrink useListOnly/>
                            </CollapsibleSection>
                        </Grid>
                        <Grid item xs={12} sm={12} md={6}>
                            <CollapsibleSection title="Receivals">
                                <Receivals title={" "} reloadFunc={reloadFunc} prnList={prnList} startDate={startDate} endDate={endDate} shrink useListOnly/>
                            </CollapsibleSection>
                        </Grid>
                    </Grid>
        </React.Fragment >
    );
};
export default Traceability;

const CollapsibleSection = ({ title, children }) => {
    const [show, setShow] = React.useState(true);
    return <JumboCardQuick
        title={<Typography variant="h2" mb={0}>{title}<Collapsable open={show} setOpen={setShow} /></Typography>}
        wrapperSx={{ p: 0, backgroundColor: 'background.paper', zIndex: 0, '&:last-child': { pb: 0 } }}
    >
        {show && children}
    </JumboCardQuick>;
};

const Collapsable = (props) => {
    const handleClick = () => {
        props.setOpen(!props.open);
    }
    if (props.open) {
        return (
            <IconButton onClick={handleClick}>
                <ExpandLessIcon />
            </IconButton>
        );

    } else {
        return (
            <IconButton onClick={handleClick}>
                <ExpandMoreIcon />
            </IconButton>
        );
    }
}
const InventoryReport = (props) => {
    const [show, setShow] = React.useState(true);
    const { data: invData, loading, error } = useLotReport('/Reports/GetStockOnHand', props.prnList, props.reload);
    const filteredInvData = useMemo(() => {
        const selected = new Set(props.prnList.filter(lot => lot.selected).map(lot => lot.LotNbr));
        return invData.filter(row => selected.has(row.lotSerialNbr)).map(row => ({
            ...row, warehouseName: row.warehouse?.description,
            stockItemName: row.stockItem?.description, stockCode: row.stockItem?.myobid
        }));
    }, [props.prnList, invData]);

    const options = {
        filterType: 'dropdown',
        selectableRowsHideCheckboxes: true,
        fixedHeader: true,
        tableBodyMaxHeight: props.height ? props.height : '400px',
        responsive: 'standard',
        rowsPerPage: 100,
    };

     options.customTableBodyFooterRender = (opts) => {
                // var proc = props.filter ? props.filter(procurement) : procurement;
                var quantityIndex = 2;
                var total = opts.data.reduce((p, c) => {
                    if (c.data[quantityIndex] !== undefined) {
                        return p + c.data[quantityIndex];
                    } else {
                        return p;
                    }
                }, 0);

                // var totals = calculateTotalSum(proc, opts.data.filter((dt) => ((dt.data[4] !== undefined))));
                const paddColStyle = {
                    color: '#000',
                    paddingTop: '1px',
                    paddingBottom: '1px'
                }
                return (
                    <TableFooter >
                        <TableRow style={{ fontWeight: 'bold' }}>
                            
                            {opts.columns.map((col, index) => {
                                if (index === 1) {
                                    return <TableCell key={index} style={{
                                        fontWeight: 'bold', ...paddColStyle
                                    }}>
                                        Total
                                    </TableCell>
                                } else if (index === quantityIndex) {
                                    return <TableCell key={index} style={{
                                        fontWeight: 'bold', ...paddColStyle
                                    }}>
                                        {numberFormat(total)}
                                    </TableCell>
                                } else {
                                    return <TableCell key={index} style={{
                                        fontWeight: 'bold', ...paddColStyle
                                    }}>
                                    </TableCell>
                                }
                            })}
                        </TableRow>
                        
                    </TableFooter>
                );
            }
    var cols = [
        {
            name: 'stockCode',
            label: 'Stock Code',
        },
        {
            name: 'stockItemName',
            label: 'Stock Item',

        },
        {
            name: 'quantity',
            label: 'Quantity',

        },

        {
            name: 'warehouseName',
            label: 'Warehouse',

        },
        {
            name: 'lotSerialNbr',
            label: 'Lot Serial Nbr',

        }
    ];
    return <JumboCardQuick
        title={<Typography variant={"h2"} mb={0}>{"Stock On Hand"}<Collapsable open={show} setOpen={setShow} /></Typography>}
        wrapperSx={{
            p: 0,
            backgroundColor: 'background.paper',
            zIndex: 0,
            '&:last-child': {
                pb: 0
            }
        }}
    >

        {error ? <Alert severity="error">{error}</Alert> : null}
        {show && (props.prnList?.length > 0) ? (loading ? <CircularProgress /> :
            <DataTableStandard
                data={filteredInvData ? filteredInvData : []}
                title={<Typography variant={"h2"} mb={0}>{""}</Typography>}
                columns={cols}
                options={options}
                returnSearch={!loading}
                reload={props.reloadFunc}
                shrink={true} />
        ) : null}
    </JumboCardQuick>
}

const ProcessingStatus = (props) => {
    const [show, setShow] = React.useState(true);
    const { data: invData, loading, error } = useLotReport('/Reports/GetStockInProcess', props.prnList, props.reload);
    const filteredInvData = useMemo(() => {
        const selected = new Set(props.prnList.filter(lot => lot.selected).map(lot => lot.LotNbr));
        return invData.filter(row => selected.has(row.lotSerialNbr)).map(row => ({
            ...row, warehouseName: row.warehouse?.description,
            stockItemName: row.stockItem?.description, stockCode: row.stockItem?.myobid,
            process: row.processLine?.isHalfshell ? 'Halfshell' : 'Live',
            processLine: row.processLine?.description
        }));
    }, [props.prnList, invData]);

    const options = {
        filterType: 'dropdown',
        selectableRowsHideCheckboxes: true,
        fixedHeader: true,
        tableBodyMaxHeight: props.height ? props.height : '400px',
        responsive: 'standard',
        rowsPerPage: 100,
    };
    options.customTableBodyFooterRender = (opts) => {
        // var proc = props.filter ? props.filter(procurement) : procurement;
        var quantityIndex = 2;
        var total = opts.data.reduce((p, c) => {
            if (c.data[quantityIndex] !== undefined) {
                return p + c.data[quantityIndex];
            } else {
                return p;
            }
        }, 0);

        // var totals = calculateTotalSum(proc, opts.data.filter((dt) => ((dt.data[4] !== undefined))));
        const paddColStyle = {
            color: '#000',
            paddingTop: '1px',
            paddingBottom: '1px'
        }
        return (
            <TableFooter >
                <TableRow style={{ fontWeight: 'bold' }}>
                  
                    {opts.columns.map((col, index) => {
                        if (index === 1) {
                            return <TableCell key={index} style={{
                                fontWeight: 'bold', ...paddColStyle
                            }}>
                                Total
                            </TableCell>
                        } else if (index === quantityIndex) {
                            return <TableCell key={index} style={{
                                fontWeight: 'bold', ...paddColStyle
                            }}>
                                {numberFormat(total)}
                            </TableCell>
                        } else {
                            return <TableCell key={index} style={{
                                fontWeight: 'bold', ...paddColStyle
                            }}>
                            </TableCell>
                        }
                    })}
                </TableRow>
                
            </TableFooter>
        );
    }
    var cols = [
        {
            name: 'stockCode',
            label: 'Stock Code',
        },
        {
            name: 'stockItemName',
            label: 'Stock Item',

        },
        {
            name: 'quantity',
            label: 'Quantity',

        },
        {
            name: 'process',
            label: 'Process'
        },
        {
            name: 'processLine',
            label: 'Line/Bench'
        },
        {
            name: 'warehouseName',
            label: 'Warehouse',

        },
        {
            name: 'lotSerialNbr',
            label: 'Lot Serial Nbr',

        }
    ];
    return <JumboCardQuick
        title={<Typography variant={"h2"} mb={0}>{"In Process"}<Collapsable open={show} setOpen={setShow} /></Typography>}
        wrapperSx={{
            p: 0,
            backgroundColor: 'background.paper',
            zIndex: 0,
            '&:last-child': {
                pb: 0
            }
        }}
    >

        {error ? <Alert severity="error">{error}</Alert> : null}
        {show && (props.prnList?.length > 0) ? (loading ? <CircularProgress /> :
            <DataTableStandard
                data={filteredInvData ? filteredInvData : []}
                title={<Typography variant={"h2"} mb={0}>{""}</Typography>}
                columns={cols}
                options={options}
                returnSearch={!loading}
                reload={props.reloadFunc}
                shrink={true} />
        ) : null}
    </JumboCardQuick>
}
const Customers = (props) => {
    const [show, setShow] = React.useState(true);
    const [loadingCustomer, setLoadingCustomer] = React.useState(false);
    const { data: invData, loading, error } = useLotReport('/Reports/GetCustomers', props.prnList, props.reload);
    const [customerError, setCustomerError] = React.useState('');
    const customerController = React.useRef(null);
    const [customerData, setCustomerData] = React.useState(null);
    const [selectedCustomer, setSelectedCustomer] = React.useState(null);
    const { theme } = useJumboTheme();
    const filteredInvData = useMemo(() => {
        const selected = new Set(props.prnList.filter(lot => lot.selected).map(lot => lot.LotNbr));
        return invData.map(row => {
            const lsnShipped = (row.lsnShipped || []).filter(line => selected.has(line.lotSerialNbr));
            const lsnUnShipped = (row.lsnUnShipped || []).filter(line => selected.has(line.lotSerialNbr));
            return {
                ...row, ...row.customer, lsnShipped, lsnUnShipped,
                quantityShipped: lsnShipped.reduce((total, line) => total + line.quantity, 0),
                quantityUnShipped: lsnUnShipped.reduce((total, line) => total + line.quantity, 0)
            };
        }).filter(row => row.lsnShipped.length || row.lsnUnShipped.length);
    }, [props.prnList, invData]);
    const filteredCustomerData = useMemo(() => {
        if (customerData === null) return null;
        const selected = new Set(props.prnList.filter(lot => lot.selected).map(lot => lot.LotNbr));
        var newOrderData = [];
        for (var i = 0; i < customerData?.orderData?.length; i++) {
            var newLines = customerData?.orderData?.[i].lines?.filter((d) => {
                return selected.has(d.lotSerialNbr);
            });
            if (newLines.length > 0) {
                newOrderData.push({ ...customerData?.orderData?.[i], lines: newLines,
                    quantity: newLines.reduce((total, line) => total + line.quantity, 0),
                    totalCost: newLines.reduce((total, line) => total + line.quantity * line.price, 0)
                });
            }
        }
        return { ...customerData, orderData: newOrderData };
    }, [customerData, props.prnList]);
    const customerLotKey = JSON.stringify(props.prnList.map(lot => lot.LotNbr));
    React.useEffect(() => {
        customerController.current?.abort();
        setCustomerData(null);
        setSelectedCustomer(null);
        setCustomerError('');
        setLoadingCustomer(false);
        return () => customerController.current?.abort();
    }, [customerLotKey, props.reload]);
    const openCustomer = async (rowData, rowMeta) => {
        var selectedCustomer = filteredInvData[rowMeta.dataIndex];
        if (!selectedCustomer) return;
        customerController.current?.abort();
        const controller = new AbortController();
        customerController.current = controller;
        setLoadingCustomer(true);
        setCustomerData(null);
        setCustomerError('');
        setSelectedCustomer(selectedCustomer);
        try {
            const data = await getLotReport('/Reports/GetCustomerSalesData', props.prnList.map(lot => lot.LotNbr), { customerId: selectedCustomer.id }, controller);
            if (!controller.signal.aborted) setCustomerData(data);
        } catch (requestError) {
            if (!controller.signal.aborted) setCustomerError(requestError.message);
        } finally {
            if (!controller.signal.aborted) setLoadingCustomer(false);
        }
    }
    const options = {
        filterType: 'dropdown',
        selectableRowsHideCheckboxes: true,
        fixedHeader: true,
        tableBodyMaxHeight: props.height ? props.height : '400px',
        responsive: 'standard',
        rowsPerPage: 100,
        onRowClick: (rowData, rowMeta) => {
            openCustomer(rowData, rowMeta);
        },
        setRowProps: row => {
            var selRow = row[0];
            if (selRow === selectedCustomer?.myobid) {
                return {
                    style: { background: 'lightYellow' }
                };
            } else {
                return {};
            }
        },
    };
    options.customTableBodyFooterRender = (opts) => {
        // var proc = props.filter ? props.filter(procurement) : procurement;
        var unshippedIndex = 2;
        var shippedIndex = 3;
        var totalUnshipped = opts.data.reduce((p, c) => {
            if (c.data[unshippedIndex] !== undefined) {
                return p + c.data[unshippedIndex];
            } else {
                return p;
            }
        }, 0);
        var totalShipped = opts.data.reduce((p, c) => {
            if (c.data[shippedIndex] !== undefined) {
                return p + c.data[shippedIndex];
            } else {
                return p;
            }
        }, 0);

        // var totals = calculateTotalSum(proc, opts.data.filter((dt) => ((dt.data[4] !== undefined))));
        const paddColStyle = {
            color: '#000',
            paddingTop: '1px',
            paddingBottom: '1px'
        }
        return (
            <TableFooter >
                <TableRow style={{ fontWeight: 'bold' }}>
                    {opts.columns.map((col, index) => {
                        if (index === 1) {
                            return <TableCell key={index} style={{
                                fontWeight: 'bold', ...paddColStyle
                            }}>
                                Total
                            </TableCell>
                        } else if (index === unshippedIndex) {
                            return <TableCell key={index} style={{
                                fontWeight: 'bold', ...paddColStyle
                            }}>
                                {numberFormat(totalUnshipped)}
                            </TableCell>
                        } else if (index === shippedIndex) {
                            return <TableCell key={index} style={{
                                fontWeight: 'bold', ...paddColStyle
                            }}>
                                {numberFormat(totalShipped)}
                            </TableCell>
                        }   else {
                            return <TableCell key={index} style={{
                                fontWeight: 'bold', ...paddColStyle
                            }}>
                            </TableCell>
                        }
                    })}
                </TableRow>
                <TableRow style={{ fontWeight: 'bold' }}>
                    {opts.columns.map((col, index) => {
                        if (index === 1) {
                            return <TableCell key={index} style={{
                                fontWeight: 'bold', ...paddColStyle
                            }}>
                                Sum Total
                            </TableCell>
                        } else if (index === unshippedIndex) {
                            return <TableCell key={index} style={{
                                fontWeight: 'bold', ...paddColStyle
                            }}>
                                {numberFormat(totalUnshipped + totalShipped)}
                            </TableCell>
                        }   else {
                            return <TableCell key={index} style={{
                                fontWeight: 'bold', ...paddColStyle
                            }}>
                            </TableCell>
                        }
                    })}
                </TableRow>
            </TableFooter>
        );
    }

    const optionsCustomer = {
        filterType: 'dropdown',
        selectableRowsHideCheckboxes: true,
        fixedHeader: true,
        tableBodyMaxHeight: props.height ? props.height : '400px',
        responsive: 'standard',
        rowsPerPage: 100,
        expandableRows: true,
        rowsExpanded: [],
        expandableRowsHeader: false,
        expandableRowsOnClick: true,
        isRowExpandable: (dataIndex, expandedRows) => {
            return true;
        },
        renderExpandableRow: (rowData, rowMeta) => {
            var lineData = filteredCustomerData?.orderData?.[rowMeta.dataIndex];
            var colspan = 1;
            return (
                <React.Fragment>
                    <TableRow >
                        <TableCell colSpan={2} style={{ padding: '0 0 0 15px', fontWeight: 'bold', fontSize: 'smaller', }} >LotSerialNbr</TableCell>
                        <TableCell colSpan={colspan} style={{ padding: '0px', fontWeight: 'bold', fontSize: 'smaller' }} >Stock Code</TableCell>
                        <TableCell colSpan={colspan} style={{ padding: '0px', fontWeight: 'bold', fontSize: 'smaller' }} >StockItem</TableCell>
                        <TableCell colSpan={colspan} style={{ padding: '0px', fontWeight: 'bold', fontSize: 'smaller' }} >Quantity</TableCell>
                        <TableCell colSpan={colspan} style={{ padding: '0px', fontWeight: 'bold', fontSize: 'smaller' }} >Price</TableCell>
                        <TableCell colSpan={colspan} style={{ padding: '0px', fontWeight: 'bold', fontSize: 'smaller' }} >Warehouse</TableCell>
                    </TableRow>

                    {
                        lineData?.lines?.map((line) => {
                            return (
                                <TableRow >
                                    <TableCell colSpan={2} style={{ padding: '0 0 0 15px', }} >{line.lotSerialNbr}</TableCell>
                                    <TableCell style={{ padding: '0px', }} >{line.stockItem?.myobid}</TableCell>
                                    <TableCell style={{ padding: '0px', }} >{line.stockItem?.description}</TableCell>
                                    <TableCell style={{ padding: '0px', }} >{line.quantity}</TableCell>
                                    <TableCell style={{ padding: '0px', }} >{line.price}</TableCell>
                                    <TableCell style={{ padding: '0px', }} >{line.warehouse?.description}</TableCell>
                                </TableRow>
                            );
                        })
                    }
                </React.Fragment>


            );

        },
        setRowProps: row => {
            var selRow = row[2];
            if (selRow === ("Not Shipped")) {
                return {
                    style: { background: theme.palette.oysterProducts.open }
                };
            } else {
                return {
                    style: { background: theme.palette.oysterProducts.completed }
                };
            }
        },
    }
    var cols = [
        {
            name: 'myobid',
            label: 'MYOBId',

        },
        {
            name: 'name',
            label: 'Customer',
        },
        {
            name: 'quantityUnShipped',
            label: 'Unshipped (doz)',

        },
        {
            name: 'quantityShipped',
            label: 'Shipped (doz)',

        }
    ];
    var colsCustomer = [
        {
            name: 'dateFormat',
            label: 'Production Date',
            options: {
                customBodyRenderLite: (dataIndex) => {
                    return filteredCustomerData?.orderData?.[dataIndex]?.productionDate ? dateFormat(filteredCustomerData?.orderData?.[dataIndex]?.productionDate) : 'N/A'
                },
            }
        },
        {
            name: 'salesOrderMYOBID',
            label: 'Sales Order',
            options: {
                customBodyRenderLite: (dataIndex) => {
                    var tenant = process.env.REACT_APP_MYOB_TENANT ? process.env.REACT_APP_MYOB_TENANT : 'https://aoc.myobadvanced.com/Main?';
                    return <Link href={tenant + 'ScreenId=SO301000&OrderType=SO&OrderNbr=' + filteredCustomerData?.orderData?.[dataIndex]?.salesOrderMYOBID} target='_blank'>{filteredCustomerData?.orderData?.[dataIndex]?.salesOrderMYOBID}</Link>
                },
            }

        },
        {
            name: 'shipmentMYOBID',
            label: 'Shipment',
            options: {
                customBodyRenderLite: (dataIndex) => {
                    var tenant = process.env.REACT_APP_MYOB_TENANT ? process.env.REACT_APP_MYOB_TENANT : 'https://aoc.myobadvanced.com/Main?';
                    if (filteredCustomerData?.orderData?.[dataIndex]?.shipmentMYOBID !== 'Not Shipped') {
                        return <Link href={tenant + 'ScreenId=SO302000&ShipmentNbr=' + filteredCustomerData?.orderData?.[dataIndex]?.shipmentMYOBID} target='_blank'>{filteredCustomerData?.orderData?.[dataIndex]?.shipmentMYOBID}</Link>
                    } else {
                        return 'Not Shipped'
                    }
                },
            }

        },

        {
            name: 'quantity',
            label: 'Quantity',
            options: {
                customBodyRenderLite: (dataIndex) => {
                    return filteredCustomerData?.orderData?.[dataIndex]?.quantity ? numberFormat((filteredCustomerData?.orderData?.[dataIndex]?.quantity)) : 'N/A'
                },
            }

        },
        {
            name: 'totalCost',
            label: 'Total Cost',
            options: {
                customBodyRenderLite: (dataIndex) => {
                    return filteredCustomerData?.orderData?.[dataIndex]?.totalCost ? ('$' + numberFormat((filteredCustomerData?.orderData?.[dataIndex]?.totalCost))) : 'N/A'
                },
            }
        },
        {
            name: '',
            label: '',

        }
    ]

    const emailMessage = (customer, data) => {
        var emailto = "";
        var emailsubject = "Product Recall";
        var emlCont = 'To: ' + emailto + '\n';

        var table = "Hi,<br/><br/>Please see below for the details of the product recall.<br/><br/>";
        table += "<table style='background:#C1E1C1;border:1px solid black;text-align:center; width:1000px; border-collapse: collapse;'><tr style='border:1px solid black;'><th style='border:1px solid black;'>Production Date</th><th style='border:1px solid black;'>Sales Order Id</th><th style='border:1px solid black;'>Shipment Id</th><th style='border:1px solid black;'>Quantity</th></tr>";
        for (var i = 0; i < data?.orderData?.length; i++) {
            table += "<tr style='background:#A7C7E7;border:1px solid black;'><td style='border:1px solid black;'>" + (data?.orderData?.[i]?.productionDate ? dateFormat(data?.orderData?.[i]?.productionDate) : 'N/A') + "</td><td style='border:1px solid black;'>" + data?.orderData?.[i]?.salesOrderMYOBID + "</td><td style='border:1px solid black;'>" + (data?.orderData?.[i]?.shipmentMYOBID !== 'Not Shipped' ? data?.orderData?.[i]?.shipmentMYOBID : 'Not Shipped') + "</td><td style='border:1px solid black;'>" + (data?.orderData?.[i]?.quantity ? (numberFormat((data?.orderData?.[i]?.quantity)) + ' doz') : 'N/A') + "</td></tr>";
            table += "<tr style='border:1px solid black;'><td colspan='4' style='border:1px solid black;'>";
            table += "<table style='font-size:smaller; width:1000px; text-align:center; border:1px solid black; border-collapse: collapse;'>";
            // table += "<tr style='border:1px solid black;'><th style='border:1px solid black;'>Stock Code</th><th style='border:1px solid black;'>Stockitem</th><th style='border:1px solid black;'>Quantity</th><th style='border:1px solid black;'>Warehouse</th></tr>";
            for (var j = 0; j < data?.orderData?.[i]?.lines?.length; j++) {
                table += "<tr style='border:1px solid black;'><td style='border:1px solid black;'>" + data?.orderData?.[i]?.lines?.[j]?.stockItem?.myobid + "</td><td style='border:1px solid black;'>" + data?.orderData?.[i]?.lines?.[j]?.stockItem?.description + "</td><td style='border:1px solid black;'>" + (data?.orderData?.[i]?.lines?.[j]?.quantity ? (numberFormat(data?.orderData?.[i]?.lines?.[j]?.quantity) + ' doz') : '') + "</td><td style='border:1px solid black;'>" + data?.orderData?.[i]?.lines?.[j]?.lotSerialNbr + "</td><td style='border:1px solid black;'>" + data?.orderData?.[i]?.lines?.[j]?.warehouse?.description + "</td></tr>";
            }
            table += "</table></td></tr>";
        }
        table += "</table>";

        emlCont += 'Subject: ' + emailsubject + '\n';
        emlCont += 'X-Unsent: 1' + '\n';

        emlCont += 'Content-Type: text/html' + '\n';
        emlCont += '' + '\n';

        emlCont += "<!DOCTYPE html><html><head></head><body>" + table + "</body></html>";
        return new Blob([emlCont], { type: 'message/rfc822' });
    }


    return <JumboCardQuick
        title={<Typography variant={"h2"} mb={0}>{"Customers"}<Collapsable open={show} setOpen={setShow} /></Typography>}
        wrapperSx={{
            p: 0,
            backgroundColor: 'background.paper',
            zIndex: 0,
            '&:last-child': {
                pb: 0
            }
        }}
    >

        {error || customerError ? <Alert severity="error">{error || customerError}</Alert> : null}
        {show && (props.prnList?.length > 0) ? (loading ? <CircularProgress /> :
            <Grid container spacing={2} mb={1} >
                <Grid item xs={12} lg={4}>
                    <DataTableStandard
                        data={filteredInvData ? filteredInvData : []}
                        title={<Typography variant={"h2"} mb={0}>{""}</Typography>}
                        columns={cols}
                        options={options}
                        returnSearch={!loading}
                        reload={props.reloadFunc}
                        shrink={true} />
                </Grid>
                <Grid item xs={12} lg={8}>
                    {loadingCustomer ? <CircularProgress /> :
                        (filteredCustomerData ? <DataTableStandard
                            data={filteredCustomerData?.orderData ? filteredCustomerData.orderData : []}
                            title={<Typography variant={"h2"} mb={0}><MailTo customerName={selectedCustomer?.name} getEmail={() => emailMessage(selectedCustomer, filteredCustomerData)} />{selectedCustomer?.name} - Orders</Typography>}
                            columns={colsCustomer}
                            options={optionsCustomer}
                            returnSearch={!loadingCustomer}
                            reload={props.reloadFunc}
                            shrink={true} /> : null)
                    }
                </Grid>
            </Grid>
        ) : null}
    </JumboCardQuick>
}

const MailTo = ({ getEmail, customerName }) => {
    const downloadEmail = () => {
        const url = URL.createObjectURL(getEmail());
        const link = document.createElement('a');
        link.href = url;
        link.download = customerName + '.eml';
        document.body.appendChild(link);
        link.click();
        link.remove();
        URL.revokeObjectURL(url);
    };
    return <IconButton title="Download recall email" onClick={downloadEmail}><MailIcon /></IconButton>;
}
